/* @jsx h */
import { defineComponent, h, provide, inject, ref, Teleport } from "vue";
import { nanoid } from "nanoid";
export type ToastType = "info" | "success" | "warning" | "error";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  autoHide?: number;
  icon?: string;
}

export interface ToastContextType {
  showToast: (
    message: string,
    options?: { type?: ToastType; autoHide?: number; icon?: string },
  ) => void;
}

const ToastContextKey = Symbol("ToastContext");

export const ToastProvider = defineComponent({
  name: "ToastProvider",
  setup(_, { slots }) {
    const toasts = ref<Toast[]>([]);

    const removeToast = (id: string) => {
      toasts.value = toasts.value.filter((toast) => toast.id !== id);
    };

    const showToast = (
      message: string,
      {
        type = "info",
        autoHide,
        icon,
      }: { type?: ToastType; autoHide?: number; icon?: string } = {},
    ) => {
      const id = nanoid();
      const toast: Toast = {
        id,
        message,
        type,
        autoHide,
        icon,
      };

      toasts.value.push(toast);

      if (autoHide) {
        setTimeout(() => removeToast(id), autoHide);
      }
    };

    provide(ToastContextKey, { showToast });

    return () => (
      <>
        {slots.default?.()}
        <Teleport to="body">
          <div class="toast toast-end z-[10000000]">
            {toasts.value.map((toast) => (
              <div
                key={toast.id}
                class={`alert ${getAlertClass(
                  toast.type,
                )} flex justify-start items-center min-w-80`}
              >
                <span class="material-symbols-outlined text-5xl">
                  {toast.icon || iconMap[toast.type] || ""}
                </span>
                <div class="flex-1 text-left">
                  <h3 class="font-bold capitalize">{toast.type}</h3>
                  <div class="text-md max-w-xs whitespace-normal break-word">
                    {toast.message}
                  </div>
                </div>
                <button
                  class="btn btn-circle btn-xs"
                  onClick={() => removeToast(toast.id)}
                >
                  <span class="material-symbols-outlined text-sm">close</span>
                </button>
              </div>
            ))}
          </div>
        </Teleport>
      </>
    );
  },
});

const iconMap: Record<string, string> = {
  error: "error",
  warning: "warning",
  success: "check_box",
  info: "info_i",
};
function getAlertClass(type: ToastType): string {
  switch (type) {
    case "success":
      return "alert-success";
    case "warning":
      return "alert-warning";
    case "error":
      return "alert-error";
    case "info":
    default:
      return "alert-info";
  }
}

export function useToast(): ToastContextType {
  const context = inject<ToastContextType>(ToastContextKey);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
